/**
 * SEO Component for dynamic meta tags
 *
 * Updates document head with:
 * - Title
 * - Meta description
 * - Open Graph tags (for social sharing)
 * - Twitter Card tags
 * - Canonical URL
 * - Structured data (JSON-LD)
 */

import { useEffect } from 'react';

/**
 * Update or create a meta tag
 * @param {string} attribute - 'name' or 'property'
 * @param {string} key - meta tag identifier
 * @param {string} content - meta tag content
 */
function setMetaTag(attribute, key, content) {
  if (!content) return;

  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

/**
 * Update or create a link tag
 * @param {string} rel - link relationship
 * @param {string} href - link URL
 */
function setLinkTag(rel, href) {
  if (!href) return;

  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

/**
 * Update or create structured data (JSON-LD)
 * @param {Object} data - structured data object
 */
function setStructuredData(data) {
  if (!data) return;

  const id = 'structured-data-jsonld';
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

/**
 * SEO Component
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description (max 160 chars)
 * @param {string} props.canonicalUrl - Canonical URL
 * @param {string} props.ogType - Open Graph type (website, article, etc.)
 * @param {string} props.ogImage - Open Graph image URL
 * @param {Object} props.structuredData - JSON-LD structured data
 * @param {boolean} props.noIndex - Add noindex meta tag
 */
export default function SEO({
  title,
  description,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  structuredData,
  noIndex = false
}) {
  useEffect(() => {
    // Site name for branding
    const siteName = 'CoinCommons';
    const fullTitle = title ? `${title} | ${siteName}` : `${siteName} - Community-Powered Crypto Safety`;

    // Update document title
    document.title = fullTitle;

    // Basic meta tags
    setMetaTag('name', 'description', description);

    // Robots
    if (noIndex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      setMetaTag('name', 'robots', 'index, follow');
    }

    // Canonical URL
    if (canonicalUrl) {
      setLinkTag('canonical', canonicalUrl);
    }

    // Open Graph tags
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:site_name', siteName);
    if (canonicalUrl) {
      setMetaTag('property', 'og:url', canonicalUrl);
    }
    if (ogImage) {
      setMetaTag('property', 'og:image', ogImage);
      setMetaTag('property', 'og:image:width', '1200');
      setMetaTag('property', 'og:image:height', '630');
    }

    // Twitter Card tags
    setMetaTag('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:description', description);
    if (ogImage) {
      setMetaTag('name', 'twitter:image', ogImage);
    }

    // Structured data
    if (structuredData) {
      setStructuredData(structuredData);
    }

  }, [title, description, canonicalUrl, ogType, ogImage, structuredData, noIndex]);

  // This component doesn't render anything
  return null;
}

/**
 * Generate structured data for a cryptocurrency/token
 * @param {Object} coin - Coin data from API
 * @returns {Object} JSON-LD structured data
 */
export function generateCoinStructuredData(coin) {
  if (!coin) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': `${coin.name} (${coin.symbol?.toUpperCase()}) - Safety Rating & Reviews`,
    'description': coin.description || `${coin.name} (${coin.symbol?.toUpperCase()}) safety rating and community reviews on CoinCommons.`,
    'url': `https://coincommons.io/coin/${coin.id}`,
    'image': coin.image?.large || coin.image,
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'CoinCommons',
      'url': 'https://coincommons.io'
    },
    'about': {
      '@type': 'Thing',
      'name': coin.name,
      'identifier': coin.id
    }
  };
}

/**
 * Generate structured data for a wallet/address
 * @param {Object} addressData - Address data
 * @param {string} chain - Blockchain name
 * @param {string} address - Wallet address
 * @returns {Object} JSON-LD structured data
 */
export function generateAddressStructuredData(addressData, chain, address) {
  const shortAddress = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': addressData?.label || `${chain} Address ${shortAddress}`,
    'description': `View safety rating and community reviews for ${chain} address ${shortAddress} on CoinCommons.`,
    'url': `https://coincommons.io/chain/${chain.toLowerCase()}/${address}`,
    'isPartOf': {
      '@type': 'WebSite',
      'name': 'CoinCommons',
      'url': 'https://coincommons.io'
    }
  };
}

/**
 * Generate structured data for the website (homepage)
 * @returns {Object} JSON-LD structured data
 */
export function generateWebsiteStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'CoinCommons',
    'description': 'Community-Powered Crypto Safety. Check crypto safety ratings, lookup wallet addresses, and read community reviews before you transact.',
    'url': 'https://coincommons.io',
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': 'https://coincommons.io/chain/ethereum/{search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    }
  };
}
